import { z } from "zod";
import type { EventRow } from "../types/event";
import { FETCH_TIMEOUT_MS } from "../config";
import { normalizeRow } from "../lib/normalize";
import { expandRecurrence } from "../lib/rrule";

const EventSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional().default(""),
  start_iso: z.string().min(1),
  end_iso: z.string().optional(),
  timezone: z.string().optional(),
  location_type: z.enum(["online", "presencial", "hibrido"]).optional().default("online"),
  location_name: z.string().optional().default(""),
  address: z.string().optional().default(""),
  city: z.string().optional().default(""),
  state: z.string().optional().default(""),
  country: z.string().optional().default(""),
  join_url: z.string().optional().default(""),
  rsvp_url: z.string().optional().default(""),
  recording_url: z.string().optional().default(""),
  speakers: z.string().optional().default(""),
  tags: z.string().optional().default(""),
  cover_image: z.string().optional().default(""),
  status: z.enum(["scheduled", "cancelled", "postponed"]).optional().default("scheduled"),
  rrule: z.string().optional(),
  exdates: z.string().optional(),
  series: z.string().optional().default(""),
  episode: z.string().optional().default(""),
  destaque: z.string().optional()
});

async function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { cache: "no-store", signal: ctrl.signal });
    return res;
  } finally {
    clearTimeout(id);
  }
}

async function retry<T>(fn: () => Promise<T>, attempts = 3, baseDelay = 500): Promise<T> {
  let lastErr: any;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, baseDelay * Math.pow(2, i)));
    }
  }
  throw lastErr;
}

export async function fetchEvents(url: string): Promise<EventRow[]> {
  const startTs = performance.now();
  const text = await retry(async () => {
    const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }, 3, 700);

  // 1) JSON puro
  let rows: EventRow[] | null = null;
  try {
    const json = JSON.parse(text);
    if (Array.isArray(json)) rows = json as EventRow[];
    else if (Array.isArray((json as any)?.data)) rows = (json as any).data as EventRow[];
  } catch { /* segue */ }

  // 2) GViz JSON (Sheets)
  if (!rows && text.includes("google.visualization.Query.setResponse(")) {
    // Remove prefixo /*O_o*/ se existir
    let cleanText = text;
    if (cleanText.startsWith("/*O_o*/")) {
      cleanText = cleanText.substring(7);
    }
    
    const s = cleanText.indexOf("google.visualization.Query.setResponse(");
    const a = cleanText.indexOf("(", s);
    const b = cleanText.lastIndexOf(")");
    const jsonStr = cleanText.slice(a + 1, b);
    const gviz = JSON.parse(jsonStr);
    const cols: string[] = (gviz.table.cols || []).map((c: any) => (c.label || c.id || "").trim());
    const rws: Array<{ c: Array<{ v: any } | null> }> = gviz.table.rows || [];

    rows = rws.map((r, idx) => {
      const obj: Record<string, any> = {};
      cols.forEach((col: string, i: number) => {
        // Pega o valor da célula, tratando null/undefined
        const cellData = r.c?.[i];
        let cellValue = cellData?.v ?? "";
        
        // Se for string, faz trim, senão mantém o valor
        if (typeof cellValue === "string") {
          cellValue = cellValue.trim();
        } else if (cellValue === null || cellValue === undefined) {
          cellValue = "";
        }
        
        // Só adiciona se a coluna não for vazia
        if (col && col.trim()) {
          obj[col] = cellValue;
        }
      });
      return normalizeRow(obj, idx);
    });
  }

  // 3) CSV simples
  if (!rows) {
    const lines = text.split(/\r?\n/).filter(Boolean);
    const [header, ...rws] = lines;
    const cols = header.split(",").map((h) => h.trim());
    rows = rws.map((r, idx) => {
      const vals = r.split(",").map((v) => v.trim());
      const obj: any = {};
      cols.forEach((c, i) => (obj[c] = vals[i] ?? ""));
      return normalizeRow(obj, idx);
    });
  }

  // Validação com zod
  const valid: EventRow[] = [];
  const errors: string[] = [];
  (rows || []).forEach((row, i) => {
    const parse = EventSchema.safeParse(row);
    if (!parse.success) {
      errors.push(`Linha ${i + 2}: ${parse.error.issues.map((it) => it.path.join('.') + ' ' + it.message).join(' | ')}`);
    } else {
      valid.push(parse.data);
    }
  });

  // Expande recorrência
  const expanded = expandRecurrence(valid);

  const latencyMs = Math.round(performance.now() - startTs);
  (window as any).__AGENDA_DIAG__ = { latencyMs, source: url, errorsCount: errors.length };
  (window as any).__AGENDA_ERRORS__ = errors;

  return expanded;
}